import type { CallAnalyticsSummary } from "@sales-ops-amplify/shared";

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function fmtDuration(sec: number): string {
  if (!sec) return "0s";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

type Props = {
  analytics?: CallAnalyticsSummary;
  dispositionName?: (id: string) => string;
  loading?: boolean;
};

export function CallsAnalyticsStrip({
  analytics,
  dispositionName,
  loading,
}: Props) {
  if (loading && !analytics) {
    return (
      <div className="rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground">
        Loading analytics…
      </div>
    );
  }
  if (!analytics) return null;

  const topDispositions = analytics.byDisposition.slice(0, 3);

  return (
    <div className="grid gap-2 rounded-lg border border-border p-3 sm:grid-cols-2 lg:grid-cols-5">
      <Stat label="Calls" value={String(analytics.total)} />
      <Stat
        label="Connect rate"
        value={pct(analytics.connectRate)}
        hint={`${analytics.connected} connected`}
      />
      <Stat
        label="Avg duration"
        value={fmtDuration(analytics.avgDurationSec)}
        hint={`${fmtDuration(analytics.totalTalkSec)} total`}
      />
      <Stat
        label="Interested"
        value={String(analytics.interested)}
        hint={`${analytics.withInsights} with insights`}
      />
      <div className="text-sm">
        <p className="text-xs text-muted-foreground">Top dispositions</p>
        {topDispositions.length === 0 ? (
          <p className="mt-1 text-muted-foreground">—</p>
        ) : (
          <ul className="mt-1 space-y-0.5 text-xs">
            {topDispositions.map((d) => (
              <li key={d.id} className="flex justify-between gap-2">
                <span className="truncate">
                  {dispositionName?.(d.id) ?? d.id}
                </span>
                <span className="text-muted-foreground">{d.count}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-1 text-[11px] text-muted-foreground">
          In {analytics.byDirection.inbound} · Out {analytics.byDirection.outbound}
        </p>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-lg font-semibold tracking-tight">{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
